// Animated syringe that fills to a volume, with graduations and (on reveal) a
// green target marker for the correct draw-up volume.
const STD_SIZES = [1, 3, 5, 10, 20, 50];
const pickSyringe = (v) => STD_SIZES.find((s) => s >= v * 1.08) || 50;
const fmtMl = (v) => (v == null || isNaN(v) ? "—" : v < 0.1 ? v.toFixed(3) : v < 1 ? v.toFixed(2) : v.toFixed(2));

export function SyringeVial({ volumeMl, correctMl, revealed, capacityMl, concLabel }) {
  const cap = capacityMl || pickSyringe(Math.max(volumeMl || 0, correctMl || 0, 0.1));
  const X0 = 26, X1 = 196; // barrel inner span
  const span = X1 - X0;
  const xFor = (v) => X0 + Math.max(0, Math.min(1, cap > 0 ? v / cap : 0)) * span;
  const fillX = xFor(volumeMl || 0);
  const ticks = Array.from({ length: 9 }, (_, i) => (i / 8) * cap);

  // over / under-draw feedback (shown on reveal)
  let draw = null;
  if (revealed && correctMl != null && !isNaN(volumeMl)) {
    const tol = Math.max(0.02, correctMl * 0.03);
    const delta = volumeMl - correctMl;
    if (Math.abs(delta) <= tol) draw = { tone: "text-emerald-600", t: "On target" };
    else if (delta > 0) draw = { tone: "text-amber-600", t: `Over-drawn by ${fmtMl(delta)} mL` };
    else draw = { tone: "text-rose-600", t: `Under-drawn by ${fmtMl(-delta)} mL` };
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="syringe-vial">
      <div className="flex items-center gap-2">
        {concLabel && (
          <div className="flex flex-col items-center shrink-0" title="Drug vial concentration" data-testid="conc-vial">
            <svg viewBox="0 0 22 40" className="w-5 h-9">
              <rect x="5" y="2" width="12" height="4" rx="1" fill="#94a3b8" />
              <rect x="4" y="6" width="14" height="32" rx="2" fill="#fff" stroke="#94a3b8" strokeWidth="1.5" />
              <rect x="4" y="20" width="14" height="18" rx="2" fill="#0047AB" opacity="0.18" />
            </svg>
            <span className="text-[8px] font-mono-clinical text-slate-500 leading-none mt-0.5 text-center max-w-[52px]">{concLabel}</span>
          </div>
        )}
        <svg viewBox="0 0 240 70" className="w-full">
          {/* barrel */}
          <rect x="20" y="22" width="184" height="26" rx="3" fill="#fff" stroke="#94a3b8" strokeWidth="2" />
          {/* fill */}
          <rect x={X0 - 2} y="24" width={Math.max(0, fillX - (X0 - 2))} height="22" fill="#0047AB" opacity="0.7" />
          {/* graduations */}
          {ticks.map((t, i) => (
            <line key={i} x1={xFor(t)} x2={xFor(t)} y1="22" y2={i % 2 === 0 ? 31 : 27} stroke="#94a3b8" strokeWidth="1" />
          ))}
          {/* target marker (correct volume) */}
          {revealed && correctMl != null && (
            <g>
              <line x1={xFor(correctMl)} x2={xFor(correctMl)} y1="16" y2="54" stroke="#15803d" strokeWidth="2.5" strokeDasharray="4 3" />
              <text x={xFor(correctMl)} y="12" textAnchor="middle" className="fill-emerald-700" style={{ fontSize: 8, fontWeight: 700 }}>{fmtMl(correctMl)}</text>
            </g>
          )}
          {/* nozzle + needle */}
          <rect x="204" y="31" width="8" height="8" fill="#94a3b8" />
          <rect x="212" y="34" width="22" height="2" fill="#64748b" />
          {/* plunger */}
          <rect x={fillX - 2} y="18" width="4" height="34" rx="1.5" fill="#475569" />
          <rect x="6" y="16" width="10" height="38" rx="2" fill="#cbd5e1" stroke="#94a3b8" strokeWidth="1" />
        </svg>
      </div>
      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{cap} mL syringe</span>
        {draw ? (
          <span className={`text-[11px] font-bold ${draw.tone}`} data-testid="syringe-draw-feedback">{draw.t}</span>
        ) : (
          <span className="text-xs font-mono-clinical font-semibold text-[#0047AB]" data-testid="syringe-vial-readout">
            {fmtMl(volumeMl)} mL
          </span>
        )}
      </div>
    </div>
  );
}
